<template>
  <div>
    <Header />
    <div
      class="container mx-auto max-w-[1200px] w-full py-7 flex flex-col gap-6 px-4 md:px-6"
    >
      <div class="flex items-center justify-end">
        <button
          @click="goBack"
          class="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-5 py-2 rounded-full transition-colors duration-200"
        >
          Back
        </button>
      </div>
      <ol class="flex items-center space-x-2 text-sm">
        <li>
          <router-link
            :to="{ name: 'EmailLogs', query: { tab: 'all' } }"
            class="inline-flex items-center font-medium text-gray-600 hover:text-blue-600 transition-all duration-200 group"
            active-class="text-blue-600"
          >
            <!-- Changed to email/envelope icon for Email Logs -->
            <svg
              class="w-4 h-4 mr-2 text-gray-400 group-hover:text-blue-500 transition-colors duration-200"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
              />
              <path
                d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
              />
            </svg>
            <span>Email Logs</span>
          </router-link>
        </li>

        <li class="flex items-center text-gray-300" aria-hidden="true">
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2.5"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </li>

        <li>
          <div
            class="flex items-center font-medium text-gray-900 bg-blue-50 px-3 py-1 rounded-full border border-blue-100"
            :title="this.$route.params.emailtitle"
          >
            <!-- Changed to document-text icon for email content -->
            <svg
              class="w-4 h-4 mr-1.5 text-blue-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="truncate max-w-xs">{{
              this.$route.params.emailtitle
            }}</span>
          </div>
        </li>
      </ol>

      <!-- Tabs -->
      <div class="flex border-b border-slate-200">
        <button
          @click="changeTab('send')"
          :class="[
            'pb-3 px-4 text-sm font-medium transition-colors duration-200',
            activeTab === 'send'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700',
          ]"
        >
          Email Send ({{ emailsendcounts }})
        </button>

        <button
          @click="changeTab('unsend')"
          :class="[
            'pb-3 px-4 text-sm font-medium transition-colors duration-200',
            activeTab === 'unsend'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700',
          ]"
        >
          Email Unsend ({{ emailunsendcounts }})
        </button>
      </div>

      <!-- Email Send Tab -->
      <div v-if="activeTab === 'send'" class="flex items-start gap-4 md:pb-20">
        <div v-if="emailSendTable.length > 0" class="w-full">
          <div
            class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-x-auto"
          >
            <table class="min-w-full text-sm">
              <thead class="bg-gray-100 whitespace-nowrap">
                <tr>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    S.No
                  </th>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    Email
                  </th>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    Display name
                  </th>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    Created at
                  </th>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    Email Status
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="(el, index) in emailSendTable"
                  :key="el.id"
                  class="hover:bg-gray-50"
                >
                  <td class="py-3 px-4 border-r text-center text-xs">
                    {{ index + 1 + (sendCurrentPage - 1) * sendPageSize }}
                  </td>
                  <td class="py-3 px-4 border-r text-center text-xs">
                    <router-link
                      :to="{
                        name: 'EmailTable',
                        params: { id: el.id },
                      }"
                      class="text-gray-800 no-underline hover:underline hover:text-blue-600"
                    >
                      {{ el.sent_to?.email || "NA" }}
                    </router-link>
                  </td>
                  <td class="py-3 px-4 border-r text-center text-xs">
                    {{ el.sent_to?.display_name || "NA" }}
                  </td>
                  <td class="py-3 px-4 border-r text-center text-xs">
                    {{ formatDate(el.created_at) || "---" }}
                  </td>
                  <td class="py-3 px-4 border-r text-center text-xs">
                    <span
                      :class="
                        el.is_triggered
                          ? 'text-green-600 bg-green-100 px-2 py-1 rounded-full'
                          : 'text-red-600 bg-red-100 px-2 py-1 rounded-full'
                      "
                    >
                      {{ el.is_triggered ? "Success" : "Failure" }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative"
          >
            <!-- PAGE SIZE -->
            <div
              class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal"
            >
              <span class="flex md:hidden">Rows per page:</span>
              <select
                v-model="sendPageSize"
                @change="onPageSizeChange('send')"
                class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
            </div>

            <!-- PAGINATION -->
            <div v-if="sendTotalPages > 1" class="flex justify-center">
              <Pagination
                :totalPages="sendTotalPages"
                :currentPage="sendCurrentPage"
                @page-changed="(page) => fetchEmailData('send', page)"
              />
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center min-h-[800px] w-full"
        >
          No Data Found
        </div>
      </div>

      <!-- Email Unsend Tab -->
      <div
        v-if="activeTab === 'unsend'"
        class="flex items-start gap-4 md:pb-20"
      >
        <div class="w-full" v-if="emailUnsendTable.length > 0">
          <div class="bg-white rounded-2xl shadow-sm overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-100 whitespace-nowrap">
                <tr>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    S.No
                  </th>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    Email
                  </th>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    Display name
                  </th>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    created at
                  </th>
                  <th
                    class="py-3 px-4 border-b border-r font-semibold text-blues text-center"
                  >
                    Email Status
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="(el, index) in emailUnsendTable"
                  :key="el.id"
                  class="hover:bg-gray-50"
                >
                  <td class="py-3 px-4 border-r text-center text-xs">
                    {{ index + 1 + (unsendCurrentPage - 1) * unsendPageSize }}
                  </td>
                  <td class="py-3 px-4 border-r text-center text-xs">
                    <router-link
                      :to="{
                        name: 'EmailTable',
                        params: { id: el.id },
                      }"
                      class="text-gray-800 no-underline hover:underline hover:text-blue-600"
                    >
                      {{ el.sent_to?.email || "NA" }}
                    </router-link>
                  </td>
                  <td class="py-3 px-4 border-r text-center text-xs">
                    {{ el.sent_to?.display_name || "NA" }}
                  </td>
                  <td class="py-3 px-4 border-r text-center text-xs">
                    {{ formatDate(el.created_at) || "---" }}
                  </td>
                  <td class="py-3 px-4 border-r text-center text-xs">
                    <span
                      :class="
                        el.is_triggered
                          ? 'text-green-600 bg-green-100 px-2 py-1 rounded-full'
                          : 'text-red-600 bg-red-100 px-2 py-1 rounded-full'
                      "
                    >
                      {{ el.is_triggered ? "Success" : "Failure" }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative"
          >
            <!-- PAGE SIZE -->
            <div
              class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal"
            >
              <span class="flex md:hidden">Rows per page:</span>
              <select
                v-model="unsendPageSize"
                @change="onPageSizeChange('unsend')"
                class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
            </div>

            <!-- PAGINATION -->
            <div v-if="unsendTotalPages > 1" class="flex justify-center">
              <Pagination
                :totalPages="unsendTotalPages"
                :currentPage="unsendCurrentPage"
                @page-changed="(page) => fetchEmailData('unsend', page)"
              />
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center min-h-[800px] w-full"
        >
          No Data Found
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "@/components/Admin/Header.vue";
import axios from "axios";
import { mapGetters } from "vuex";
import Pagination from "@/components/Pagination.vue";

export default {
  components: { Header, Pagination },
  data() {
    return {
      emailSendTable: [],
      emailUnsendTable: [],
      activeTab: "send",
      sendCurrentPage: 1,
      unsendCurrentPage: 1,
      sendTotalPages: 1,
      unsendTotalPages: 1,
      sendPageSize: 10,
      unsendPageSize: 10,
      emailsendcounts: 0,
      emailunsendcounts: 0,
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    emailSendCount() {
      return this.emailSendTable?.length || 0;
    },
    emailUnsendCount() {
      return this.emailUnsendTable?.length || 0;
    },
  },
  mounted() {
    this.fetchAllEmailData();
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return "-";
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    async changeTab(tab) {
      this.activeTab = tab;

      if (tab === "send") {
        this.sendCurrentPage = 1;
        await this.fetchEmailData("send", 1);
      } else {
        this.unsendCurrentPage = 1;
        await this.fetchEmailData("unsend", 1);
      }
    },

    onPageSizeChange(type) {
      if (type === "send") {
        this.sendCurrentPage = 1;
        this.fetchEmailData("send", 1);
      } else {
        this.unsendCurrentPage = 1;
        this.fetchEmailData("unsend", 1);
      }
    },

    async fetchEmailData(type, page = 1) {
      const isTriggered = type === "send";
      let pageSize = type === "send" ? this.sendPageSize : this.unsendPageSize;
      // Update current page based on type
      if (type === "send") {
        this.sendCurrentPage = page;
      } else {
        this.unsendCurrentPage = page;
      }

      let params = {
        Key: this.userInfo.token_key,
        page: page,
        page_size: pageSize,
        campaign__id: this.$route.params.id,
        is_triggered: isTriggered,
      };

      if (this.searching) {
        params.search = this.searching;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/email-recipient/list/`,
          { params },
        );

        if (response?.data?.data?.status === 200) {
          const data = response?.data?.data?.data || [];

          if (type === "send") {
            this.emailSendTable = data;
            this.sendTotalPages = response?.data?.total_page || 1;
            this.sendCurrentPage = response.data.current_page;
            this.emailsendcounts = response.data.total;

          } else {
            this.emailUnsendTable = data;
            this.unsendTotalPages = response?.data?.total_page || 1;
            this.unsendCurrentPage = response.data.current_page;
            this.emailunsendcounts = response.data.total;
          }
        }
      } catch (error) {
        console.error(`Error fetching ${type} emails:`, error);

        if (type === "send") {
          this.emailSendTable = [];
          this.sendTotalPages = 1;
        } else {
          this.emailUnsendTable = [];
          this.unsendTotalPages = 1;
        }
      }
    },

    async fetchAllEmailData() {
      await Promise.all([
        this.fetchEmailData("send", this.sendCurrentPage),
        this.fetchEmailData("unsend", this.unsendCurrentPage),
      ]);
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped></style>
